import { useEffect, useState } from 'react';

import moment from 'moment';

import { useHelpBundle } from '@plasma/ui.application.app';
import { useNavigate } from '@plasma/ui.application.router';
import notification from '@plasma/ui.feedback.notification';
import { useResourceBundle, useTranslation } from '@plasma/ui.utils.i18n';

import { fetchAllDataGet_byApiName } from '../../services/gets/getdata';
import { Plan, PlanType, planTypeNumberMap, Product, ProductDistribution, ProfileSelection } from '../../services/Interface/productionFigureInterface';
import { sendAllDataPost_byApiNameAndData } from '../../services/posts/postdata';
import translations from './i18n';
import { ProductionFigureProps } from './production-figure';
import help from './production-figure.help';
import { apiUriMasterDataHttp, apiUriProductionPlanHttp } from '../../services/conf/apiConf';

function useProductionFigureHook(props: ProductionFigureProps) {
  useResourceBundle(translations, undefined, 'f3c5678d-7c5b-46f7-b6d9-2efe8f8dd666');
  useHelpBundle(help);
  const navigate = useNavigate();

  const getCurrentDateTime = () => {
    return new Date().toISOString().replace('T', ' ').replace('Z', '').substring(0, 19).replace(' ', '_');
  }

  const { t } = useTranslation();
  const [distributions, setDistributions] = useState<ProductDistribution[]>([]);
  const [totalMatricTons, setTotalMatricTons] = useState<number>(0);
  const [productList, setProductList] = useState<Product[]>([]);
  const [IsPlanCreate, setIsPlanCreate] = useState<boolean>(false);
  const [existPlan, setExistPlan] = useState<Plan | null>(null);
  const [selectedValue, setSelectedValue] = useState<PlanType>('year');
  const [planlist, setPlanlist] = useState<Plan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [planName, setPlanName] = useState<string>('');
  const [planType, setPlanType] = useState<number>(3);
  const [manuallyEditedCards, setManuallyEditedCards] = useState<Set<number>>(new Set());
  const [existPlanCopy, setExistPlanCopy] = useState<Plan | null>(null);
  const [planNameError, setPlanNameError] = useState<string | null>(null);
  const [showplanTemplateModal, setShowplanTemplateModal] = useState<boolean>(false);
  const [showplanSuffixModal, setShowplanSuffixModal] = useState<boolean>(false);
  const [suffixPlanName, setSuffixPlanName] = useState<string>(getCurrentDateTime());
  const [loading, setLoading] = useState<boolean>(false);
  const [userRoles, setuserRoles] = useState<string[]>([]);
  const isSuperUser = sessionStorage.getItem('superUserRole');

  const [isEditModalVisible, setIsEditModalVisible] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<'new' | 'edit' | 'copy' | 'calc' | null>(null);
  const [showManagePlanModal, setShowManagePlanModal] = useState<boolean>(false);
  const [isCalcButtonDisabled, setIsCalcButtonDisabled] = useState(false);

  const parseDate = (dateString: string | undefined) => {
    if (!dateString) return null;
    const correctedDateString = `${dateString.slice(0, 19)}Z`;
    return moment(correctedDateString);
  };

  const handlePlanTypeChange = async (value: PlanType, existPlan_l?: Plan) => {
    const totalMetricTonsFromAPI = Number(await fetchAllDataGet_byApiName('GetConstantValue?constantName=PRODUCTION', apiUriProductionPlanHttp()));
    let calculatedTons = 0;
    const planTypeFromMap = planTypeNumberMap.find((item) => item.value === value);
    const divisor = planTypeFromMap?.divisor;

    setSelectedValue(value);
    setPlanType(planTypeFromMap?.typeId ?? 3);

    if (value === 'date') {
      const startDate = parseDate(existPlan_l?.planFromDate);
      const endDate = parseDate(existPlan_l?.planToDate);
      const diffInDays = endDate?.diff(startDate, 'days');
      calculatedTons = (totalMetricTonsFromAPI / 364) * (diffInDays || 1);
    } else {
      calculatedTons = totalMetricTonsFromAPI / (divisor || 1);
    }

    setTotalMatricTons(calculatedTons);
    handleTotalMetricTonsChange(calculatedTons, existPlan_l);
  };

  const handleSelectChange = (value: string) => {
    const selectedPlan = planlist.find((plan) => plan.Planname === value);
    if (selectedPlan) {
      setSelectedPlanId(selectedPlan.Planid);
      setPlanName(value);
    }
  };

  const fetchProductTypes = async () => {
    try {
      const response = await fetchAllDataGet_byApiName('GetMasterProductDetails', apiUriMasterDataHttp());
      setProductList(response);
      const distinctTypes: string[] = [];

      response.forEach((res: any) => {
        if (!distinctTypes.includes(res.producttype)) {
          distinctTypes.push(res.producttype);
        }
      });

      setDistributions(distinctTypes.map(type => ({
        productType: type,
        distributedValue: 0,
        calPerCentDistribute: 0,
        remainingPercentage: 100,
        profileSelections: []
      })));
      return response;
    } catch (error) {
      console.error('Error fetching data:', error);
      return [];
    }
  };

  const handleTotalMetricTonsChange = (tonnage: string | number, existPlan_l?: Plan | null) => {
    const value = Number(tonnage);
    setTotalMatricTons(value);

    if (IsPlanCreate) {
      setDistributions(prev => prev.map(d => ({
        ...d,
        distributedValue: 0,
        calPerCentDistribute: 0,
        remainingPercentage: 100,
        profileSelections: []
      })));
      calculateDemand(value);
    } else if (existPlan_l) {
      setExistPlan({ ...existPlan_l, plannedTonnage: value });

      setDistributions(prev => prev.map(d => {
        const factor = value / (existPlan_l.plannedTonnage || 1);
        return {
          ...d,
          distributedValue: d.distributedValue ? d.distributedValue * factor : 0,
          profileSelections: d.profileSelections.map(p => ({
            ...p,
            profileTonnage: p.profileTonnage * factor
          }))
        };
      }));
    } else {
        calculateDemand(value);
    }
  };

  const calculateDemand = (tonnage: number) => {
    setDistributions(prev => {
      if (prev.length === 0) return prev;
      const calculate = Number((tonnage / prev.length).toFixed(2));
      const calPercent = Number(((calculate / tonnage) * 100).toFixed(2));

      return prev.map(d => ({
        ...d,
        distributedValue: calculate,
        calPerCentDistribute: calPercent,
        remainingPercentage: 100
      }));
    });
  };

  const handleProfileValueChange = (index: number, value: number) => {
    setDistributions(prev => {
      const totalPlannedTonnage = IsPlanCreate ? totalMatricTons : (existPlan?.plannedTonnage || totalMatricTons);
      if (!totalPlannedTonnage) return prev;

      const newDist = [...prev];
      const newManuallyEdited = new Set(manuallyEditedCards);

      if (!newManuallyEdited.has(index)) {
        newManuallyEdited.add(index);
        setManuallyEditedCards(newManuallyEdited);
      }

      const newPercentage = Number(((value / totalPlannedTonnage) * 100).toFixed(2));

      let totalEditedPercentage = 0;
      newManuallyEdited.forEach(i => {
        if (i !== index) totalEditedPercentage += newDist[i].calPerCentDistribute;
      });

      if (totalEditedPercentage + newPercentage > 100) {
        const max = Math.floor(((100 - totalEditedPercentage) * totalPlannedTonnage) / 100);
        notification.error(`${t("maximum-possible-value-is")} ${max} ${t("tons")}`);
        return prev;
      }

      newDist[index] = { ...newDist[index], distributedValue: value, calPerCentDistribute: newPercentage };

      let totalAssignedTonnage = 0;
      newManuallyEdited.forEach(i => { totalAssignedTonnage += newDist[i].distributedValue; });

      const remainingValue = Math.max(0, totalPlannedTonnage - totalAssignedTonnage);
      const untouchedCards = prev.map((_, i) => i).filter(i => !newManuallyEdited.has(i) && i !== index);

      if (untouchedCards.length > 0) {
        const valuePerCard = Number((remainingValue / untouchedCards.length).toFixed(2));
        const percentPerCard = Number(((valuePerCard / totalPlannedTonnage) * 100).toFixed(2));

        untouchedCards.forEach(i => {
          newDist[i] = { ...newDist[i], distributedValue: valuePerCard, calPerCentDistribute: percentPerCard };
        });
      }

      return newDist.map((d) => {
        const used = d.profileSelections.reduce((sum, s) => sum + s.profilePercent, 0);
        return { ...d, remainingPercentage: Math.max(0, 100 - used) };
      });
    });
  };

  const handleProfileAdd = (index: number, profileDim: string, profileTonnage: number) => {
    if (!profileDim || !profileTonnage) return;

    setDistributions(prev => {
      const newDist = [...prev];
      const dist = { ...newDist[index] };
      const profilePercent = Number(((profileTonnage / dist.distributedValue) * 100));

      if (profilePercent > dist.remainingPercentage) {
        notification.error(t("profile-percentage-exceeds-remaining-percentage"));
        return prev;
      }

      dist.profileSelections = [...dist.profileSelections, { profileDim, profileTonnage, profilePercent }];
      const used = dist.profileSelections.reduce((sum, s) => sum + s.profilePercent, 0);
      dist.remainingPercentage = Math.max(0, 100 - used);
      newDist[index] = dist;
      return newDist;
    });
  };

  const handleProfileDelete = (index: number, profileIndex: number) => {
    setDistributions(prev => {
      const newDist = [...prev];
      const dist = { ...newDist[index] };
      dist.profileSelections = [...dist.profileSelections];
      dist.profileSelections.splice(profileIndex, 1);

      const used = dist.profileSelections.reduce((sum, s) => sum + s.profilePercent, 0);
      dist.remainingPercentage = Math.max(0, 100 - used);
      newDist[index] = dist;
      return newDist;
    });
  };

  const comparePlanWithProducts = (productList: Product[], plan: Plan) => {
    if (!productList || !plan) return;

    const newManuallyEdited = new Set<number>();
    const distData = plan.productWiseDistributePlan || [];

    setDistributions(prev => {
      const newDist = [...prev];

      newDist.forEach((d, i) => {
        const matched = distData.filter(p => p.producType === d.productType);
        if (matched.length > 0) {
           newManuallyEdited.add(i);
           const profiles: ProfileSelection[] = [];
           let distTons = 0;
           let distPercent = 0;

           matched.forEach(m => {
              profiles.push({
                profileDim: productList.find(p => p.productcode === m.productCode)?.productdescription || '',
                profileTonnage: Number(m.profileTonnage),
                profilePercent: Number(m.profilePercent)
              });
              distTons = Number(m.productTonnage);
              distPercent = Number(m.productPercent);
           });

           const uniqueProfiles = profiles.filter((v,i,a)=>a.findIndex(v2=>(v2.profileDim===v.profileDim))===i);

           const used = uniqueProfiles.reduce((sum, s) => sum + s.profilePercent, 0);
           newDist[i] = {
             ...d,
             distributedValue: distTons,
             calPerCentDistribute: distPercent,
             profileSelections: uniqueProfiles,
             remainingPercentage: Math.max(0, 100 - used)
           };
        } else {
           newDist[i] = {
             ...d,
             distributedValue: 0,
             calPerCentDistribute: 0,
             profileSelections: [],
             remainingPercentage: 100
           };
        }
      });
      setManuallyEditedCards(newManuallyEdited);
      return newDist;
    });
  };

  const LoadData = async() => {
    try {
      const plans = await fetchAllDataGet_byApiName('GetPlan', apiUriProductionPlanHttp());
      setPlanlist(plans);
      const fetchedProductList = await fetchProductTypes();
      const plan = await fetchAllDataGet_byApiName('GetProductionPlan', apiUriProductionPlanHttp());
      if (plan) {
        setPlanName(plan.planName || '');
        setTotalMatricTons(plan.plannedTonnage || 0);
        await handlePlanTypeChange(planTypeNumberMap.find((item) => item.typeId === plan.planType)?.value || 'year', plan);
        setExistPlan(plan);
        comparePlanWithProducts(fetchedProductList, plan);
      }
    } catch (error) {
      console.error('Error initialization', error);
    }
  };

  useEffect(() => {
    LoadData();
    const storedData = sessionStorage.getItem('UserRoles');
    if (storedData) setuserRoles(JSON.parse(storedData));
  }, []);

  const fetchProductionPlanById = async (planId: number) => {
    try {
      const plan = await fetchAllDataGet_byApiName(`GetProductionPlan?planId=${planId}`, apiUriProductionPlanHttp());
      if (plan) {
        setPlanName(plan.planName || '');
        setTotalMatricTons(plan.plannedTonnage || 0);
        setExistPlan(plan);
        await handlePlanTypeChange(planTypeNumberMap.find((item) => item.typeId === plan.planType)?.value || 'year', plan);
        comparePlanWithProducts(productList, plan);
        setIsPlanCreate(false);
      }
    } catch (error) {
      console.error('Error fetching production plan by ID:', error);
    }
  };

  useEffect(() => {
    if (selectedPlanId !== null) fetchProductionPlanById(selectedPlanId);
  }, [selectedPlanId]);

  useEffect(() => {
    const checkCalculationLimit = async () => {
      if (existPlan?.planId) {
        try {
          const snapshots = await fetchAllDataGet_byApiName(`GetPlanSnapshots?templateId=${existPlan.planId}`, apiUriProductionPlanHttp());
          if (snapshots && Array.isArray(snapshots) && snapshots.length >= 1) {
            setIsCalcButtonDisabled(true);
            return;
          }
        } catch (error) {
          console.error('Failed to check calculation snapshots count:', error);
        }
      }
      setIsCalcButtonDisabled(false);
    };

    checkCalculationLimit();
  }, [existPlan?.planId]);

  const cleanEverything = async () => {
    if (existPlan) setExistPlanCopy(existPlan);
    setManuallyEditedCards(new Set());
    setTotalMatricTons(0);
    setSelectedValue('year');
    setPlanName('');
    setIsPlanCreate(false);
    setExistPlan(null);
    setDistributions(prev => prev.map(d => ({
      ...d, distributedValue: 0, calPerCentDistribute: 0, remainingPercentage: 100, profileSelections: []
    })));
  };

  const handleCancel = () => {
    if (existPlanCopy) {
      setExistPlan(existPlanCopy);
      setPlanName(existPlanCopy.planName || '');
      setTotalMatricTons(existPlanCopy.plannedTonnage || 0);
      comparePlanWithProducts(productList, existPlanCopy);
      setIsPlanCreate(false);
    }
    setIsEditModalVisible(false);
    setEditMode(null);
  };

  const handleNewPlanCreate = async () => {
    setShowManagePlanModal(false);
    cleanEverything();
    setIsPlanCreate(true);
    setPlanName('');
    const t = await fetchAllDataGet_byApiName('GetConstantValue?constantName=PRODUCTION', apiUriProductionPlanHttp());
    if(t) {
      setTotalMatricTons(t);
      calculateDemand(t);
    }
    setEditMode('new');
    setIsEditModalVisible(true);
  };

  const handleEditPlan = () => {
    setShowManagePlanModal(false);
    setIsPlanCreate(false);
    setEditMode('edit');
    setIsEditModalVisible(true);
  };

  // const handleCopyPlan = () => {
  //   setShowManagePlanModal(false);
  //   setEditMode('copy');

  //   const currentName = planName || '';
  //   const baseName = currentName.split(' | ')[0].trim();

  //   let maxN = 0;
  //   planlist.forEach((p: any) => {
  //     const pName = p.Planname || '';
  //     if (pName.startsWith(`${baseName} | `)) {
  //       const suffixPart = pName.substring(baseName.length + 3);
  //       const match = suffixPart.match(/^C (\d+)$/i);
  //       if (match) {
  //         const num = parseInt(match[1], 10);
  //         if (num > maxN) maxN = num;
  //       }
  //     }
  //   });

  //   const nextN = maxN + 1;
  //   setSuffixPlanName(`C ${nextN}`);
  //   setShowplanSuffixModal(true);
  // };

  const handleCopyPlan = () => {
    setShowManagePlanModal(false);
    setEditMode('copy');

    const currentName = planName || '';
    const baseName = currentName.split(' | ')[0].trim();

    let maxN = 0;
    planlist.forEach((p: any) => {
      const pName = p.Planname || '';
      if (pName.startsWith(`${baseName} | `)) {
        const suffixPart = pName.substring(baseName.length + 3);
        const match = suffixPart.match(/^C (\d+)$/i);
        if (match) {
          const num = parseInt(match[1], 10);
          if (num > maxN) maxN = num;
        }
      }
    });

    const nextN = maxN + 1;

    setEditMode('copy');
    // setSuffixPlanName(`C ${nextN}`);
    setIsPlanCreate(true);
    setPlanName(`${baseName} | C${nextN}`);
    setShowplanSuffixModal(false);
    setIsEditModalVisible(true);
  };

  const handleCalculationSnapshot = async () => {
    setShowManagePlanModal(false);
    setEditMode('calc');

    const baseName = (planName || '').trim();

    let maxN = 0;

    if (existPlan?.planId) {
      try {
        const snapshots = await fetchAllDataGet_byApiName(`GetPlanSnapshots?templateId=${existPlan.planId}`, apiUriProductionPlanHttp());
          if (snapshots && Array.isArray(snapshots)) {
            if (snapshots.length >= 1) {
              setIsCalcButtonDisabled(true);
              return;
            }

            snapshots.forEach((snap: any) => {
            const snapName = snap.SnapshotName || snap.snapshot_name || snap.Planname || snap.planName || '';
            if (snapName.startsWith(`${baseName} | `)) {
              const suffixPart = snapName.substring(baseName.length + 3);
              const match = suffixPart.match(/^Calc (\d+)$/i);
              if (match) {
                const num = parseInt(match[1], 10);
                if (num > maxN) maxN = num;
              }
            }
          });
        }
      } catch (error) {
        console.error('Failed to fetch snapshots for calc generation:', error);
      }
    }

    const nextN = maxN + 1;
    setSuffixPlanName(`Calc ${nextN}`);
    setShowplanSuffixModal(true);
  };

  // const executeCopyPlan = (suffix: string) => {
  //   setIsPlanCreate(true);
  //   const baseName = planName.split(' | ')[0].trim();
  //   setPlanName(`${baseName} | ${suffix}`);
  //   setEditMode('copy');
  //   setShowplanSuffixModal(false);
  //   setIsEditModalVisible(true);
  // };

  const calculateProfilesForSave = () => {
    return distributions.flatMap(d => {
      return d.profileSelections.map(s => {
        const code = productList.find(p => p.producttype === d.productType && p.productdescription === s.profileDim)?.productcode;
        return {
          producType: d.productType,
          productCode: code || '',
          productPercent: d.calPerCentDistribute,
          productTonnage: d.distributedValue,
          profilePercent: s.profilePercent,
          profileTonnage: ((d.distributedValue * s.profilePercent) / 100).toFixed(1),
          inputBillet: '',
          isActive: true
        };
      });
    });
  };

  const handleSubmit = async (suffix: string) => {
    if (!selectedValue || !planName) {
      notification.error(t("please-fill-required-fields"));
      return;
    }
    const plannedTonnage = IsPlanCreate ? totalMatricTons : (existPlan?.plannedTonnage || 0);
    const planType = planTypeNumberMap.find((item) => item.value === selectedValue)?.typeId || 4;
    const pType = planTypeNumberMap.find(item => item.value === selectedValue);

    let finalPlanName = planName;
    if (editMode === 'copy') {
      finalPlanName = planName; // Already formatted by handleCopyPlan
    } else if (editMode === 'calc' && suffix !== '') {
      finalPlanName = `${planName}${suffix}`;
    } else if (suffix !== '') {
      const baseName = planName.split(' | ')[0].trim();
      finalPlanName = `${baseName}${suffix}`;
    }

    const submitData = {
      id: existPlan?.id || null,
      planId: IsPlanCreate ? 0 : (existPlan?.planId || 0),
      planName: finalPlanName,
      plannedTonnage,
      plannedPercent: 100,
      plannedDate: new Date().toISOString(),
      plannedBy: '',
      plannedForQuarter: IsPlanCreate ? null : (existPlan?.plannedForQuarter || null),
      plannedForMonth: IsPlanCreate ? null : (existPlan?.plannedForMonth || null),
      plannedForYear: IsPlanCreate ? (selectedValue === 'year' ? new Date().getFullYear() : null) : (existPlan?.plannedForYear || null),
      planFromDate: pType?.value === 'date' ? `${existPlan?.planFromDate?.slice(0, 19)}Z` : '',
      planToDate: pType?.value === 'date' ? `${existPlan?.planToDate?.slice(0, 19)}Z` : '',
      planType,
      IsActive: true,
      remarks: null,
      ProductWiseDistributePlan: calculateProfilesForSave()
    };

    try {
      const response = await sendAllDataPost_byApiNameAndData('add', submitData, apiUriProductionPlanHttp());
      notification.success(t("saved-successfully"));
      if (response?.planId) {
        // Refresh the dropdown plan list
        const plans = await fetchAllDataGet_byApiName('GetPlan', apiUriProductionPlanHttp());
        setPlanlist(plans);

        await fetchProductionPlanById(response.planId);
        setIsPlanCreate(false);
        setShowplanTemplateModal(false);
        setIsEditModalVisible(false);
        setEditMode(null);
      }
    } catch (e) {
      notification.error(t("failed-to-save"));
    }
  };

  const handleCreateTemplate = async(suffix: string) => {
    let finalSuffix = suffix;
    if (!finalSuffix || finalSuffix.trim() === '') {
      finalSuffix = `Calc ${Math.floor(10000 + Math.random() * 90000)}`;
    }

    setShowplanSuffixModal(false);
    setLoading(true);
    await handleSubmit(` | ${finalSuffix}`);
    navigate(`/planned-demand-page/${existPlan?.planId}/${finalSuffix}/${existPlan?.planName}`);
    setLoading(false);
  };

  const handleDeletePlanAndTemplate = async () => {
    if (!selectedValue || !planName) return;
    const plannedTonnage = IsPlanCreate ? totalMatricTons : (existPlan?.plannedTonnage || 0);
    const pType = planTypeNumberMap.find(item => item.typeId === planType);

    const submitData = {
      id: null,
      planId:  (existPlan?.planId || 0),
      planName, plannedTonnage, plannedPercent: 100,
      plannedDate: new Date().toISOString(), plannedBy: '',
      plannedForQuarter: IsPlanCreate ? null : (existPlan?.plannedForQuarter || null),
      plannedForMonth: IsPlanCreate ? null : (existPlan?.plannedForMonth || null),
      plannedForYear: IsPlanCreate ? (selectedValue === 'year' ? new Date().getFullYear() : null) : (existPlan?.plannedForYear || null),
      planFromDate: pType?.value === 'date' ? `${existPlan?.planFromDate?.slice(0, 19)}Z` : '',
      planToDate: pType?.value === 'date' ? `${existPlan?.planToDate?.slice(0, 19)}Z` : '',
      planType, IsActive: true, remarks: null,
      ProductWiseDistributePlan: calculateProfilesForSave()
    };
    try {
      await sendAllDataPost_byApiNameAndData('delete', submitData, apiUriProductionPlanHttp());
      notification.success(t("deleted-successfully"));
      LoadData();
    } catch (e) {
      notification.error(t("failed-to-delete"));
    }
  };

  const handlePlanNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPlanName(e.target.value);
    const isDup = planlist.some(p => p.Planname.trim().toLowerCase() === e.target.value.trim().toLowerCase());
    setPlanNameError(isDup && e.target.value.trim() !== '' ? t('This plan name already exists.') : null);
  };

  const handlePlanTemplateModalCancel = () => setShowplanTemplateModal(false);
  const handleSaveButton = () => IsPlanCreate ? handleSubmit('') : handleSubmit('');

  return {
    t, handlePlanNameChange, planNameError, handlePlanTypeChange,
    distributions, setDistributions,
    totalMatricTons, handleTotalMetricTonsChange, handleProfileValueChange,
    productList, existPlan, setExistPlan, IsPlanCreate,
    selectedValue, handleSelectChange, planlist, planName, setPlanName, parseDate,
    handleSubmit, comparePlanWithProducts, handleSaveButton, handleDeletePlanAndTemplate,
    handleCreateTemplate, handleCancel, suffixPlanName, setSuffixPlanName, getCurrentDateTime,
    handleNewPlanCreate, showplanSuffixModal, setShowplanSuffixModal, loading,
    showplanTemplateModal, setShowplanTemplateModal, handlePlanTemplateModalCancel, userRoles, isSuperUser,
    handleProfileAdd, handleProfileDelete, isCalcButtonDisabled,
    isEditModalVisible, setIsEditModalVisible, editMode, handleEditPlan, handleCopyPlan, handleCalculationSnapshot,
    showManagePlanModal, setShowManagePlanModal,
  };
}

export default useProductionFigureHook;
